/*
  Permission semantics (mirrors everfi-frontend's reactifi menuHelper):

  permissions: [ { condA }, { condB } ]  → condA OR condB
  { has_capabilities: [...], has_rule_set: [...] }  → caps AND rule_set

  Keys:
    has_capabilities     — org must have ≥1 capability (OR within array)
    not_capabilities     — org must NOT have any of these capabilities
    has_rule_set         — user must be in that rule set / group (COMMON_VALS key or direct name)
    not_rule_set         — user must NOT be in that rule set / group
    has_rule_set_role    — user must have PRIMARY role in rule set / group
    not_rule_set_role    — user must NOT have primary role

  IMPORTANT: has_capabilities / not_capabilities must come first in each permissions object.

  has_rule_set value formats:
    'group_name'                   — any rule set in COMMON_VALS[group_name]
    { group_name: 'specific_name' }  — only 'specific_name' within that COMMON_VALS group
    'event_manager_not_cra_content_only' — special: event_manager + org NOT cra-content-only

  URL templates resolved by filterMenu:
    {{slug}}         — org slug
    {{engage_base}}  — withAccountHost(slug, engageDomain, locale)
    {{next_base}}    — withAccountHost(slug, nextDomain, '')  (no trailing slash)
*/

const COMMON_VALS = Object.entries({
  engage_managers: ['event_manager', 'event_staff'],
  next_managers: ['at_work_manager', 'at_work_advisor', 'pii_restricted_next_primary', 'pii_restricted_next_advisor'],
  next_main_managers: ['at_work_manager', 'pii_restricted_next_primary'],
  advisors: ['at_work_advisor', 'pii_restricted_next_advisor'],
  he_cc_fs_learner_names: ['he_learner', 'cc_learner', 'fac_staff_learner'],
  he_cc_fs_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin'],
  he_cc_fs_scorm_manager_names: ['he_admin', 'cc_admin', 'fac_staff_admin', 'scorm_manager'],
  he_cc_fs_primary_managers: ['he_admin', 'cc_admin', 'fac_staff_admin'],
  k12: ['k12_admin', 'k12_teacher'],
}).map(([key, value]) => ({ [key]: value }));

export const PERMITTED_WHITELIST = [
  '/cportal/customizations',
  '/cportal/actionable_dashboard',
  '/cportal/registrations',
];

export class PermissionManager {
  constructor(user, organization, currentRuleSetName) {
    this.user = user;
    this.organization = organization;
    this.currentRuleSetName = currentRuleSetName;
    const caps = organization?.capability_types || [];
    this.craContentOnly = caps.includes('CRAEducation') && !caps.includes('CRATracking');
  }

  hasCapability = value => {
    if (!this.organization) return false;
    const caps = this.organization.capability_types || [];
    return value.some(val => caps.includes(val));
  };

  // Supports both fi-live format (rule_sets array + rule_set_roles hash {id => role})
  // and reactifi format (user_rule_set_roles array of {rule_set: name, role: string}).
  hasRuleSetRoleNameMatch = (ruleSetRoles, name) => {
    return ruleSetRoles.some(
      rule =>
        rule.rule_set === name &&
        (!this.currentRuleSetName || this.currentRuleSetName === name) &&
        rule.role === 'primary'
    );
  };

  hasRuleSetNameMatch = (ruleSets, name, isAppSwitcher) => {
    const selectedRuleSetMatches = isAppSwitcher || !this.currentRuleSetName || this.currentRuleSetName === name;
    return ruleSets.some(item => item.name === name && selectedRuleSetMatches);
  };

  hasNameMatch = (type, name, isAppSwitcher) => {
    if (!this.user) return false;
    const { rule_sets, rule_set_roles, user_rule_set_roles } = this.user;

    if (type === 'has_rule_set_role' || type === 'not_rule_set_role') {
      // reactifi format: user_rule_set_roles is [{rule_set: name, role: string}]
      if (user_rule_set_roles) {
        return this.hasRuleSetRoleNameMatch(user_rule_set_roles, name);
      }
      // fi-live format: rule_sets array + rule_set_roles hash {id => 'primary'/'secondary'}
      if (rule_sets && rule_set_roles) {
        const rs = rule_sets.find(rule_set => rule_set.name === name);
        if (!rs) return false;
        const isCurrentMatch = !this.currentRuleSetName || this.currentRuleSetName === name;
        const isPrimary = rule_set_roles[String(rs.id)] === 'primary';
        // Return positive check only; processRulesConditionals inverts for not_rule_set_role
        return isCurrentMatch && isPrimary;
      }
      return false;
    }

    if (rule_sets && (type === 'has_rule_set' || type === 'not_rule_set')) {
      return this.hasRuleSetNameMatch(rule_sets, name, isAppSwitcher);
    }

    return false;
  };

  searchCommonVals = (type, key, value, isAppSwitcher) => {
    if (key === 'event_manager_not_cra_content_only') {
      if (this.craContentOnly) return false;
      return this.hasNameMatch(type, 'event_manager', isAppSwitcher);
    }

    let checked = false;
    COMMON_VALS.forEach(entry => {
      if (entry[key]) {
        if (value) {
          checked = this.hasNameMatch(type, value, isAppSwitcher);
        } else {
          checked = entry[key].some(name => this.hasNameMatch(type, name, isAppSwitcher));
        }
      }
    });

    // If not a COMMON_VALS group key, treat as a direct rule set name
    if (!checked && !COMMON_VALS.some(e => e[key])) {
      checked = this.hasNameMatch(type, key, isAppSwitcher);
    }

    return checked;
  };

  hasSetOrRole = (key, value, isAppSwitcher) => {
    value = Array.isArray(value) ? value : [value];
    let access = false;
    value.every(val => {
      if (typeof val === 'object') {
        // e.g. { he_cc_fs_scorm_manager_names: 'scorm_manager' }
        const groupKey = Object.keys(val)[0];
        const specificVal = val[groupKey];
        access = this.searchCommonVals(key, groupKey, specificVal, isAppSwitcher);
      } else if (key !== 'url' && key !== 'path_includes') {
        access = this.searchCommonVals(key, val, null, isAppSwitcher);
      }
      return !access;
    });
    return access;
  };

  processRulesConditionals = (key, value, isAppSwitcher) => {
    const hasAccess = this.hasSetOrRole(key, value, isAppSwitcher);
    if (key === 'not_rule_set' || key === 'not_rule_set_role') return !hasAccess;
    return hasAccess;
  };

  processCapabilitiesConditionals = (key, value) => {
    const hasAccess = this.hasCapability(value);
    if (key === 'not_capabilities') return !hasAccess;
    return hasAccess;
  };

  hasAccess = (permissions, isAppSwitcher) => {
    let hasAccess = true;
    permissions.every(permission => {
      hasAccess = true;
      Object.entries(permission).forEach(([key, value]) => {
        if (key === 'has_capabilities' || key === 'not_capabilities') {
          hasAccess = this.processCapabilitiesConditionals(key, value);
        }
        if (hasAccess && (key === 'has_rule_set_role' || key === 'not_rule_set_role')) {
          hasAccess = this.processRulesConditionals(key, value, isAppSwitcher);
        }
        if (hasAccess && (key === 'has_rule_set' || key === 'not_rule_set')) {
          hasAccess = this.processRulesConditionals(key, value, isAppSwitcher);
        }
      });
      return !hasAccess;
    });
    return hasAccess;
  };
}

const withAccountHost = (subdomain, appDomain, path) => {
  try {
    const url = new URL(appDomain);
    const port = url.port && url.port !== '80' && url.port !== '443' ? `:${url.port}` : '';
    return `${url.protocol}//${subdomain}.${url.hostname}${port}/${path}`;
  } catch (e) {
    return `${appDomain}/${path}`;
  }
};

// Maps app-switcher item ids to the role set names used to pick the correct current_rule_set_name.
// Mirrors reactifi's menuRoles constant.
const menuRoles = {
  train: ['he_admin', 'cc_admin', 'fac_staff_admin', 'scorm_manager'],
  impact: ['executive'],
  learn: ['he_learner', 'cc_learner', 'fac_staff_learner'],
  'promote-advisor': ['at_work_advisor', 'pii_restricted_next_advisor'],
  'promote-next': ['at_work_manager', 'pii_restricted_next_primary'],
  engage: ['event_manager', 'event_staff'],
};

// Sets the correct current_rule_set_name for each app-switcher sub-item based on
// the user's available rule sets for that app (strips any existing param first).
const setUrlWithAvailableRuleSet = (ruleSets, roleNames, subMenu) => {
  if (!roleNames || !ruleSets) return subMenu;
  const available = ruleSets.filter(rule => roleNames.includes(rule.name));
  if (!available.length) return subMenu;
  const baseUrl = subMenu.target_url?.split('?')[0];
  return { ...subMenu, target_url: `${baseUrl}?current_rule_set_name=${available[0].name}` };
};

const resolveTemplates = (url, slug, engageBase, nextBase) => {
  if (!url) return url;
  return url
    .replace('{{slug}}', slug || '')
    .replace('{{engage_base}}', engageBase || '')
    .replace('{{next_base}}', nextBase || '');
};

const addCurrentRuleSetName = (targetUrl, currentRuleSetName) => {
  if (!currentRuleSetName) return targetUrl;
  try {
    const url = new URL(targetUrl);
    if (!url.searchParams.get('current_rule_set_name')) {
      url.searchParams.set('current_rule_set_name', currentRuleSetName);
    }
    return url.toString();
  } catch (e) {
    return targetUrl;
  }
};

export const filterMenu = (menuDefinition, args) => {
  const { user: rawUser, organization, currentRuleSetName, locale = 'en', engageDomain, nextDomain } = args;
  // Normalize JSON:API format (adminifi_user serializes as {id, attributes: {...}})
  const user = rawUser?.attributes ? { id: rawUser.id, ...rawUser.attributes } : rawUser;
  const permissionManager = new PermissionManager(user, organization, currentRuleSetName);

  const slug = organization?.slug;
  const engageBase = slug && engageDomain ? withAccountHost(slug, engageDomain, locale).replace(/\/$/, '') : '';
  const nextBase = slug && nextDomain ? withAccountHost(slug, nextDomain, '').replace(/\/+$/, '') : '';

  const processItem = (item, isAppSwitcherChild = false) => {
    if (item.permissions) {
      if (!permissionManager.hasAccess(item.permissions, isAppSwitcherChild)) return null;
    }

    const processed = { ...item };

    if (processed.submenu_items?.length > 0) {
      const childIsAppSwitcher = processed.id === 'app-switcher';
      processed.submenu_items = processed.submenu_items
        .map(sub => processItem(sub, childIsAppSwitcher))
        .filter(Boolean);
    }
    // Resolve {{user_name}} title template
    if (processed.title?.includes('{{user_name}}')) {
      const name = user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
      processed.title = processed.title.replace('{{user_name}}', name);
    }

    // For app-switcher, resolve templates on sub-items first, then inject current_rule_set_name.
    // Hide the entire switcher if only one app is available (nothing to switch to).
    if (processed.id === 'app-switcher' && processed.submenu_items?.length > 0) {
      processed.submenu_items = processed.submenu_items.map(sub => {
        const resolvedUrl = resolveTemplates(sub.target_url, slug, engageBase, nextBase);
        return setUrlWithAvailableRuleSet(user?.rule_sets, menuRoles[sub.id], { ...sub, target_url: resolvedUrl });
      });
      if (processed.submenu_items.length <= 1) return null;
    }
    if (processed.target_url) {
      processed.target_url = resolveTemplates(processed.target_url, slug, engageBase, nextBase);
      if (!processed.new_window) {
        processed.target_url = addCurrentRuleSetName(processed.target_url, currentRuleSetName);
      }
    }

    // Resolve help URL: advisors with an org resource URL get that instead of the default
    if (processed.help_url_post_processing && processed.target_url) {
      const isAdvisor = permissionManager.hasAccess([{ has_rule_set: ['advisors'] }]);
      if (isAdvisor && organization?.advisor_resource_url) {
        processed.target_url = organization.advisor_resource_url;
      }
    }

    if (processed.target_url || processed.submenu_items?.length > 0) {
      return processed;
    }

    return null;
  };

  const filteredItems = (menuDefinition.submenu_items || []).map(item => processItem(item)).filter(Boolean);

  return {
    ...menuDefinition,
    submenu_items: filteredItems,
  };
};

export const withPermissionIndicator = (menuData, currentUrl) => {
  const allItems = menuData.submenu_items.concat(menuData.submenu_items.flatMap(item => item.submenu_items || []));
  let currentPath = '';
  if (currentUrl) {
    try {
      currentPath = new URL(currentUrl).pathname;
    } catch (e) {
      // relative or invalid URL — use as-is
      currentPath = currentUrl.split('?')[0];
    }
  }
  const current_menu_item_permitted =
    currentPath.length > 0 && allItems.some(item => item.target_url && item.target_url.includes(currentPath));

  return {
    ...menuData,
    current_menu_item_permitted,
    permitted_whitelist: PERMITTED_WHITELIST,
  };
};
